/**
 * Flutter InAppWebView Callback Utility
 * 
 * This utility provides a centralized way to interact with the Flutter app
 * through the InAppWebView bridge with proper error handling.
 */

/**
 * Check if Flutter InAppWebView is available
 * @returns {boolean} - Returns true if Flutter InAppWebView is available
 */
export const isFlutterAvailable = () => {
  return (
    typeof window !== 'undefined' &&
    window.flutter_inappwebview &&
    typeof window.flutter_inappwebview.callHandler === 'function'
  );
};

/**
 * Navigate to a route within the Flutter app using handleInAppDeeplink
 * @param {string} route - The route to navigate to
 * @param {boolean} pop - Whether to pop the current webview (default: true)
 * @returns {Promise} - Returns a promise that resolves when navigation is complete
 */
export const navigateToRoute = (route, pop = true) => {
  return new Promise((resolve, reject) => {
    try {
      // Check if flutter_inappwebview is available
      if (isFlutterAvailable()) {
        window.flutter_inappwebview.callHandler('handleInAppDeeplink', {
          route: route,
          pop: pop
        })
        .then(() => {
          console.log(`Navigating to ${route}, pop: ${pop}`);
          resolve();
        })
        .catch((error) => {
          console.error('Navigation error:', error);
          reject(error);
        });
      } else {
        const errorMsg = 'flutter_inappwebview not available';
        console.error(errorMsg);
        reject(new Error(errorMsg));
      }
    } catch (error) {
      console.error('Navigation error:', error);
      reject(error);
    }
  });
};

/**
 * Open a URL in the Flutter app or fallback to browser
 * @param {string} url - The URL to open
 * @param {string} fallbackUrl - Optional fallback URL if Flutter is not available
 * @param {boolean} showAlert - Whether to show an alert when Flutter is not available (default: false)
 */
export const openUrl = (url, fallbackUrl = null, showAlert = false) => {
  try {
    if (isFlutterAvailable()) {
      // Try to navigate using Flutter
      navigateToRoute(url, true)
        .catch((error) => {
          console.error('Error navigating in Flutter app:', error);
          // Fallback to opening in browser
          if (fallbackUrl || url) {
            window.open(fallbackUrl || url, '_blank');
          }
        });
    } else {
      // Flutter not available, use fallback
      if (showAlert) {
        alert('This feature is only available when running inside the app webview');
      }
      console.log('Flutter not available, using fallback URL');
      if (fallbackUrl || url) {
        window.open(fallbackUrl || url, '_blank');
      }
    }
  } catch (error) {
    console.error('Error opening URL:', error);
    if (showAlert) {
      alert('Error navigating: ' + error.message);
    }
    // Final fallback to opening in browser
    if (fallbackUrl || url) {
      window.open(fallbackUrl || url, '_blank');
    }
  }
};

/**
 * Legacy support: Open a deeplink in the Flutter app
 * This is for backward compatibility with the existing openDeepLink pattern
 * @param {string} url - The deeplink URL to open
 * @returns {Promise} - Returns a promise that resolves when the deeplink is opened
 */
export const openDeepLink = (url) => {
  return new Promise((resolve, reject) => {
    try {
      if (isFlutterAvailable()) {
        window.flutter_inappwebview.callHandler('openDeepLink', {
          url: url
        })
        .then(() => {
          console.log(`Opening deeplink: ${url}`);
          resolve();
        })
        .catch((error) => {
          console.error('Deeplink error:', error);
          reject(error);
        });
      } else {
        const errorMsg = 'Flutter InAppWebView not available';
        console.error(errorMsg);
        reject(new Error(errorMsg));
      }
    } catch (error) {
      console.error('Deeplink error:', error);
      reject(error);
    }
  });
};

export default {
  isFlutterAvailable,
  navigateToRoute,
  openUrl,
  openDeepLink
};
